import { useRef, useState } from 'react'

import { Box, PauseIcon, PlayIcon, Waveform } from '@hivewatch/dark'

type ColorScheme = 'dark' | 'light'

interface AudioPlayerIProps {
  url: string
  colorScheme?: ColorScheme
  onPlay?: () => void
}

export const AudioPlayer = ({
  url,
  colorScheme = 'dark',
  onPlay,
}: AudioPlayerIProps) => {
  const wsRef = useRef<unknown>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const isDark = colorScheme === 'dark'

  const activeBg = isDark
    ? 'bg.tab-active'
    : 'light.interactive.tertiary-hover-bg'

  const handlePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      if (!wsRef.current) return
      onPlay?.()
      ;(wsRef.current as { playPause: () => void }).playPause()
      setIsPlaying((p) => !p)
    } catch {
      // wavesurfer may be in an invalid state
    }
  }

  return (
    <Box alignItems='center' display='flex' gap='8px' mt='8px' w='100%'>
      <Box
        _active={
          isPlaying
            ? {}
            : {
                bg: activeBg,
                color: isDark ? 'icon.active' : undefined,
              }
        }
        _hover={
          isPlaying
            ? {}
            : {
                bg: isDark
                  ? 'interactive.tertiary-hover-bg'
                  : 'light.interactive.tertiary-hover-bg',
                borderColor: isDark
                  ? 'interactive.tertiary-hover-border'
                  : undefined,
                color: isDark ? 'icon.primary' : undefined,
              }
        }
        alignItems='center'
        as='button'
        bg={isPlaying ? activeBg : undefined}
        border='1px solid'
        borderColor={isDark ? 'border.subtle' : 'light.border.subtle'}
        color={
          isPlaying
            ? isDark
              ? 'icon.active'
              : 'light.icon.secondary'
            : isDark
            ? 'icon.secondary'
            : 'light.icon.secondary'
        }
        cursor='pointer'
        data-testid='audioPlayer_playPauseButton'
        display='flex'
        flexShrink={0}
        h='36px'
        justifyContent='center'
        onClick={handlePlayPause}
        transition='background 0.15s ease, border-color 0.15s ease, color 0.15s ease'
        w='36px'
      >
        {isPlaying ? (
          <PauseIcon color='currentColor' size={14} />
        ) : (
          <PlayIcon color='currentColor' size={14} />
        )}
      </Box>
      <Waveform
        colorScheme={colorScheme}
        onFinish={() => setIsPlaying(false)}
        onReady={(ws) => {
          wsRef.current = ws
        }}
        src={url}
        style={{ cursor: 'default' }}
      />
    </Box>
  )
}
